import React, { useState } from "react";
import InstructorLayout from "../../layouts/InstructorLayout";
import { Clock3, ChevronLeft } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useGetInstructorSessionModulesDetailQuery,
  useMarkInstructorSessionModuleCompleteMutation,
} from "../../services/instructor/instructor";
import {
  CalenderSession1,
  CalenderSession2,
  VedioSession1,
  VedioSession2,
} from "../../components/instructor/icons";
import { MarkCompleted } from "../../components/Icons";

const SessionDetails = () => {
  const navigate = useNavigate();
  const { id: sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionType = searchParams.get("type") || "live";
  const queryStudentName = searchParams.get("student") || "";
  const queryStudentImage = searchParams.get("studentImage") || "";
  const queryPreferredTime = searchParams.get("preferredTime") || "";
  const queryPreferredDate = searchParams.get("preferredDate") || "";
  const queryDate = searchParams.get("date") || "";
  const queryTime = searchParams.get("time") || "";
  const { data: sessionModulesResponse, isLoading: isModulesLoading } =
    useGetInstructorSessionModulesDetailQuery(sessionId, {
      skip: !sessionId,
    });
  const [markModuleComplete, { isLoading: isMarkingComplete }] =
    useMarkInstructorSessionModuleCompleteMutation();
  const [locallyCompleted, setLocallyCompleted] = useState(() => new Set());

  const sessionModules = Array.isArray(sessionModulesResponse?.data?.modules)
    ? sessionModulesResponse.data.modules
    : [];
  const session = sessionModulesResponse?.data?.session
    ? sessionModulesResponse.data.session
    : null;
  const completedSessions = sessionModules.filter(
    (module) => module?.isCompleted,
  ).length;
  const totalSessions = sessionModules.length || 1;
  const remainingSessions = Math.max(totalSessions - completedSessions, 0);
  const useQueryDetails = sessionType === "demo";
  const studentImageValue = useQueryDetails
    ? queryStudentImage
    : session?.student?.profilePicture?.secureUrl;
  const studentNameValue = useQueryDetails
    ? queryStudentName
    : `${session?.student?.firstName || ""} ${session?.student?.lastName || ""}`.trim();
  const timeValue = useQueryDetails
    ? queryPreferredTime || queryTime
    : session?.startTime && session?.endTime
      ? `${session.startTime} to ${session.endTime}`
      : "";
  const dateValue = useQueryDetails
    ? queryPreferredDate || queryDate
    : session?.sessionDate;
  const dateDisplay = dateValue
    ? new Date(dateValue)
        .toLocaleDateString("en-GB", {
          timeZone: "UTC",
        })
        .replace(/\//g, "-")
    : "";
  const sessionLink = session?.sessionLink || "";

  const handleJoinSession = () => {
    if (!sessionLink) return;
    window.open(sessionLink, "_blank", "noopener,noreferrer");
  };

  return (
    <InstructorLayout>
      <div className="pl-[38px] pr-24 pt-8 pb-14 bg-[#EAEAEA14] min-h-screen">
        {/* Breadcrumb */}
        <div className="text-sm tracking-[-1%] text-[#6E7485] mb-11">
          Dashboard <span className="mx-4">/</span>
          <span className="text-[#1D2026]">Manage Sessions</span>
        </div>

        {/* Heading */}
        <div className="flex items-center gap-5 mb-8 pl-3">
          <button onClick={() => navigate(-1)}>
            <ChevronLeft size={30} className="text-[#089BC4]" />
          </button>

          <h1 className="text-[30px] leading-5 font-medium font-['Inter'] text-black">
            Session Details
          </h1>
        </div>

        {/* Course Progress */}
        <div className="bg-white text-black font-['Inter'] rounded-lg border border-[#D9D9D9] py-6 pl-8 pr-12 mb-6 ml-3">
          <h2 className="text-[26px] leading-5 font-medium mb-5">
            Course Progress
          </h2>

          <div className="flex justify-between text-lg leading-5 items-center mb-3">
            <span>Progress</span>

            <span>
              {completedSessions} of {totalSessions} Sessions
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-[#D9D9D9] rounded-full h-4 mb-3 overflow-hidden">
            <div
              className="bg-[#089BC4] h-full  rounded-full"
              style={{
                width: `${
                  totalSessions > 0
                    ? (completedSessions / totalSessions) * 100
                    : 0
                }%`,
              }}
            ></div>
          </div>

          <p className="text-lg text-black font-light">
            {remainingSessions} Sessions Remaining
          </p>
        </div>

        <div className="bg-white rounded-lg p-8 mb-6 flex flex-col lg:flex-row justify-between items-center gap-8 shadow-[0_0_9.8px_1px_#00000040]">
          <div className="flex items-center gap-4">
            <img
              src={studentImageValue}
              alt="student"
              className="w-32 h-32 rounded-full object-cover"
            />

            <div className="text-black">
              <h3 className="font-['Inter'] text-2xl font-semibold ">
                {studentNameValue}
              </h3>

              <p className="text-base  mt-1.5">Student</p>
            </div>
          </div>

          <div className="flex flex-col gap-8 text-base leading-5 text-black">
            {/* <div className="flex items-center gap-8">
              <CalenderSession2 />
              <p className="flex items-center gap-6">
                <span>0 Months</span>

                <span>Mon- Friday</span>
              </p>
            </div> */}

            <div className="flex items-center gap-8">
              <Clock3 className="w-7 h-7 " />

              <span>{timeValue}</span>
            </div>
          </div>
        </div>

        {/* Live Session Card */}
        <div className="bg-[#F5F5F5] rounded-lg shadow-[0_0_9.8px_1px_#00000040] px-16 pb-16 pt-14">
          <div className="flex items-center gap-5 mb-7">
            <VedioSession1 />

            <h2 className="text-[32px] font-medium leading-[56px] text-black">
              {sessionType === "demo" ? "Demo Sessions" : "Live Sessions"}
            </h2>
          </div>

          <div className="flex items-center gap-5 mb-7">
            <CalenderSession1 />

            <p className="text-[32px] font-medium leading-[56px] text-black">
              {dateDisplay} – {useQueryDetails ? queryTime : session?.startTime}
            </p>
          </div>

          {/* <div className="flex items-center gap-5 mb-5">
            <Clock3 className="w-9 h-9 text-[#EEE603]" />

            <p className="text-[32px] font-bold leading-[56px] text-[#EEE603]">
              Starts In 00:00:00
            </p>
          </div> */}

          <button
            type="button"
            onClick={handleJoinSession}
            disabled={!sessionLink || isModulesLoading}
            className="bg-[#089BC4] hover:bg-[#0789af] transition-all text-white text-2xl font-bold px-24 py-4 flex items-center gap-7 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <VedioSession2 />
            {sessionType === "demo" ? "Join Demo Class" : "Join Live Class"}
          </button>
        </div>

        {/* Session Modules */}
        <div className="mt-10">
          {isModulesLoading ? (
            <div className="flex items-center justify-center h-[40vh]">
              <div className="w-10 h-10 border-4 border-[#089BC4] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {sessionModules.map((module, index) => {
                const isCompleted =
                  module?.isCompleted || locallyCompleted.has(module?._id);
                return (
                  <div
                    key={module?._id || index}
                    className={`rounded-[18px]  px-4 py-5 flex items-center justify-between gap-6 shadow-[0_0_9.8px_1px_#00000040] ${
                      isCompleted ? "bg-[#D2F9E0]" : "bg-[#F9F9F9]"
                    }`}
                  >
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 rounded-[18px] bg-white flex items-center justify-center text-[32px] font-bold text-black shadow-[0_0_9.8px_1px_#00000040]">
                      {module?.moduleIndex ?? index + 1}
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-[#000] mb-1">
                        {module?.moduleName}
                      </h3>
                      <p className="text-[#6E7485]">
                        {module?.moduleDescription}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className={`w-14 h-14 bg-white rounded-xl flex items-center justify-center border shadow-[0_0_9.8px_1px_#00000040] transition-opacity ${
                      isCompleted || isMarkingComplete
                        ? " cursor-not-allowed"
                        : "hover:opacity-90"
                    }`}
                    onClick={async () => {
                      if (!module?._id || !sessionId || isCompleted) {
                        return;
                      }
                      try {
                        await markModuleComplete({
                          sessionId,
                          moduleId: module._id,
                        }).unwrap();
                        setLocallyCompleted((prev) => {
                          const next = new Set(prev);
                          next.add(module._id);
                          return next;
                        });
                      } catch (error) {
                        // Keep server state on failure.
                      }
                    }}
                    disabled={isCompleted || isMarkingComplete}
                  >
                    {isCompleted ? <MarkCompleted /> : null}
                  </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </InstructorLayout>
  );
};

export default SessionDetails;
